/**
 * Admin → Home (ADMIN_REDESIGN_PLAN.md). Admins: FTDs, deposits and withdrawals over a chosen time frame, the
 * queues staff clear by hand, and trading volume by hour against yesterday (GET /api/admin/home). Dealers: the book, where the hedge
 * falls short of target, and the accounts to watch (GET /api/admin/desk/risk).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { addDays, addMonths, D, dayOf, fmtUsd, fmtUsdSigned, monthStart, rangeLabel, rangeQuery, todayIn, type DayRange } from '@platform/shared';
import { ClientCell } from '../components/ClientCell';
import { isCustom, TimeFrameSelect, type Period } from '../components/DateRange';
import { InstrumentLabel } from '../components/InstrumentIcon';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { AdminHomeData, Tab } from './AdminNav';

/** "$4.82M", "$12.4K", "$860". */
function compactUsd(x: string): string {
  const v = D.from(x);
  const a = v.abs().toNumber();
  const sign = v.isNeg() ? '−' : '';
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e4) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return sign + fmtUsd(v.abs(), 0);
}
const ago = (iso: string | null) => {
  if (!iso) return null;
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  return m < 60 ? `${m} m` : m < 48 * 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${Math.floor(m / 1440)} days`;
};
const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function usePoll<T>(path: string, everyMs: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    const load = async () => {
      try {
        const d = await api.get<T>(path);
        if (!gone) (setData(d), setError(null));
      } catch (e) {
        if (!gone) setError(e instanceof ApiError ? e.message : 'Failed to load');
      }
    };
    void load();
    const t = window.setInterval(load, everyMs);
    return () => {
      gone = true;
      window.clearInterval(t);
    };
  }, [path, everyMs]);
  return { data, error };
}

// ---------- Square-style pieces ----------

/** `clip={false}` lets a menu in the header hang below the card (round the content's own corners then). */
export function Card({ title, right, children, className, clip = true }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; clip?: boolean }) {
  return (
    <section className={cx('min-w-0 rounded-[20px] bg-panel', clip && 'overflow-hidden', className)}>
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-6 py-5">
          <h2 className="m-0 text-[21px] font-normal">{title}</h2>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/** One figure in a card row: label, big number, grey line. A button when it leads somewhere. */
function Cell({ label, value, sub, icon, onClick }: { label: string; value: string; sub?: ReactNode; icon?: 'dark' | 'light'; onClick?: () => void }) {
  const body = (
    <>
      <span className={cx('flex items-center gap-3 font-semibold', icon ? 'pb-5 text-[17px]' : 'text-base')}>
        {icon && (
          <span className={cx('grid size-[34px] place-items-center rounded-full', icon === 'dark' ? 'bg-[#56627a] text-white' : 'bg-[#f0f0f0] text-fg')}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d={icon === 'dark' ? 'M12 4v16M16 8c-1-1-2-1.5-4-1.5-2.5 0-3.5 1-3.5 2.5s1 2 3.5 2.5 3.5 1 3.5 2.5-1 2.5-3.5 2.5c-2 0-3-.5-4-1.5' : 'M12 5v14M5 12h14'} />
            </svg>
          </span>
        )}
        {label}
      </span>
      <span className="num text-[32px] leading-tight font-bold tracking-tight">{value}</span>
      {sub && <span className="text-[15px] text-muted">{sub}</span>}
    </>
  );
  const cls = 'flex min-w-0 flex-col gap-1.5 border-line p-6 text-left text-fg [&:not(:last-child)]:border-b sm:[&:not(:last-child)]:border-r sm:[&:not(:last-child)]:border-b-0';
  return onClick ? (
    <button type="button" onClick={onClick} className={cx(cls, 'hover:bg-[#fafafa]')}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

function Delta({ now, before, money }: { now: string | number; before: string | number; money?: boolean }) {
  const d = D.from(String(now)).sub(String(before));
  if (d.isZero()) return <span className="text-muted">—</span>;
  const up = d.isPos();
  return (
    <span className="num">
      {money ? fmtUsdSigned(d.toFixed(2), 0) : `${up ? '+' : '−'}${d.abs().toString()}`}{' '}
      <span className={up ? 'text-[#4c9a1a]' : 'text-down'} aria-label={up ? 'up' : 'down'}>
        {up ? '▲' : '▼'}
      </span>
    </span>
  );
}

function Headline({ label, value, up, strong }: { label: string; value: string; up: boolean | null; strong?: boolean }) {
  return (
    <div className={cx('flex flex-col gap-1', !strong && 'text-muted')}>
      <span className="text-[17px] font-medium">
        {up !== null && (
          <span className={cx('mr-1', up ? (strong ? 'text-[#4c9a1a]' : 'text-[#8cc56a]') : 'text-down')} aria-label={up ? 'up on yesterday' : 'down on yesterday'}>
            {up ? '▲' : '▼'}
          </span>
        )}
        {label}
      </span>
      <span className="num text-[32px] leading-tight font-bold tracking-tight">{value}</span>
    </div>
  );
}

/** Volume by hour, today (black, up to now) against yesterday (grey), with a hover read-out. */
function VolumeChart({ hours, nowHour }: { hours: AdminHomeData['trading']['hours']; nowHour: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = 260;
  const max = Math.max(1, ...hours.flatMap((h) => [Number(h.today), Number(h.yesterday)]));
  const x = (i: number) => (i / 23) * W;
  const y = (v: string) => H - 6 - (Number(v) / max) * (H - 40);
  const line = (key: 'today' | 'yesterday', upto: number) =>
    hours
      .slice(0, upto + 1)
      .map((h, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(h[key]).toFixed(1)}`)
      .join(' ');
  const at = hover ?? nowHour;
  const h = hours[at]!;
  return (
    <figure className="relative m-0" aria-label="Trading volume by hour, today against yesterday (UTC)">
      <div className="pointer-events-none absolute top-0 flex flex-col gap-1 text-[13px]" style={{ left: `min(calc(${(at / 23) * 100}% + 8px), calc(100% - 200px))` }}>
        <span className="text-xs font-semibold">{hourLabel(at)} UTC</span>
        <span>
          <span className="font-semibold">Volume</span> <span className="num">{fmtUsd(h.today, 0)}</span>
          <span className="text-muted"> · yesterday </span>
          <span className="num text-muted">{fmtUsd(h.yesterday, 0)}</span>
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="mt-10 block h-[260px] w-full"
        aria-hidden="true"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setHover(Math.max(0, Math.min(23, Math.round(((e.clientX - r.left) / r.width) * 23))));
        }}
        onMouseLeave={() => setHover(null)}
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="#ececec" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={line('yesterday', 23)} fill="none" stroke="#dedede" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        <path d={line('today', nowHour)} fill="none" stroke="#101010" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        <line x1={x(at)} x2={x(at)} y1="0" y2={H} stroke="#9a9a9a" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />
      </svg>
      {at <= nowHour && <span className="pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg" style={{ left: `${(at / 23) * 100}%`, top: `calc(40px + ${(y(h.today) / H) * 260}px)` }} />}
      <div className="num relative h-8 text-sm text-muted">
        {[0, 3, 6, 9, 12, 15, 18, 21].map((hh) => (
          <span key={hh} className={cx('absolute top-3 whitespace-nowrap', hh > 0 && '-translate-x-1/2')} style={{ left: `${(hh / 23) * 100}%` }}>
            {hourLabel(hh)}
          </span>
        ))}
      </div>
      <figcaption className="flex gap-4 pt-3 text-[13px] text-muted">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3.5 bg-fg" />
          Today
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3.5 bg-[#dedede]" />
          Yesterday
        </span>
      </figcaption>
      <table className="sr-only">
        <caption>Volume by hour (UTC)</caption>
        <thead>
          <tr>
            <th>Hour</th>
            <th>Today</th>
            <th>Yesterday</th>
          </tr>
        </thead>
        <tbody>
          {hours.map((r) => (
            <tr key={r.hour}>
              <td>{hourLabel(r.hour)}</td>
              <td>{fmtUsd(r.today, 0)}</td>
              <td>{fmtUsd(r.yesterday, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function SubHead({ title, note, right }: { title: string; note: string; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 pb-1">
      <div className="flex items-baseline gap-2.5">
        <h2 className="m-0 text-[17px] font-semibold text-[#4a4a4a]">{title}</h2>
        <span className="text-[15px] text-muted">{note}</span>
      </div>
      {right && <span className="text-[15px] text-[#4a4a4a]">{right}</span>}
    </div>
  );
}

const Loading = ({ error }: { error: string | null }) =>
  error ? (
    <p className="m-0 rounded-[20px] bg-panel p-6 text-sm text-down" role="alert">
      {error}
    </p>
  ) : (
    <div className="grid place-items-center rounded-[20px] bg-panel p-16 text-muted">
      <Spinner />
    </div>
  );

// ---------- Admin ----------

/** The Money card's time frame, in UTC days. */
const MONEY_PERIODS: { id: 'today' | 'yesterday' | '7' | '30' | 'month' | 'lastMonth'; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
];
type MoneyPeriod = Period<(typeof MONEY_PERIODS)[number]['id']>;

function moneyDays(p: MoneyPeriod): DayRange {
  if (isCustom(p)) return p.range;
  const t = todayIn('utc');
  switch (p.id) {
    case 'today':
      return { from: t, to: t };
    case 'yesterday':
      return { from: addDays(t, -1), to: addDays(t, -1) };
    case 'month':
      return { from: monthStart(t), to: t };
    case 'lastMonth':
      return { from: addMonths(monthStart(t), -1), to: addDays(monthStart(t), -1) };
    default:
      return { from: addDays(t, -(Number(p.id) - 1)), to: t };
  }
}

/** "▲ vs 18 Oct 17": the previous period's figure, arrowed by which way it went. */
function Versus({ now, before, label, show }: { now: string | number; before: string | number; label: string; show: string }) {
  const d = D.from(String(now)).sub(String(before));
  return (
    <span className="block">
      {!d.isZero() && (
        <span className={d.isPos() ? 'text-[#4c9a1a]' : 'text-down'} aria-label={d.isPos() ? 'up' : 'down'}>
          {d.isPos() ? '▲' : '▼'}{' '}
        </span>
      )}
      vs {label} <span className="num">{show}</span>
    </span>
  );
}

export function AdminHome({ go, hidden }: { go: (t: Tab) => void; hidden: Tab[] }) {
  const [period, setPeriod] = useState<MoneyPeriod>({ id: 'today' });
  const days = moneyDays(period);
  const { data: h, error } = usePoll<AdminHomeData>(`/api/admin/home?${rangeQuery(days, 'utc')}`, 60_000);
  if (!h) return <Loading error={error} />;
  // An API from before FTDs (the web app can go out ahead of the backend): say so rather than fail.
  if (!h.money?.ftd) return <Loading error="Home is waiting for the latest server update. Reload in a few minutes." />;
  const t = h.trading;
  const q = h.queues;
  const m = h.money;
  const nowHour = new Date(h.asOf).getUTCHours();
  const today = todayIn('utc');
  const shown: DayRange = { from: dayOf(new Date(m.from), 'utc'), to: addDays(dayOf(new Date(m.to), 'utc'), -1) };
  const before = rangeLabel({ from: dayOf(new Date(m.previousFrom), 'utc'), to: addDays(shown.from, -1) }, today);
  const avg = m.ftd.count ? D.from(m.ftd.amount).div(m.ftd.count).toFixed(2) : '0';
  const can = (tab: Tab) => !hidden.includes(tab);
  const queues = [
    can('withdrawals') && { label: 'Withdrawals', count: q.withdrawals.count, sub: q.withdrawals.count ? `${fmtUsd(q.withdrawals.amount, 0)} · oldest ${ago(q.withdrawals.oldestAt)}` : 'Nothing waiting', tab: 'withdrawals' as Tab },
    (can('kyc') || can('sof')) && { label: 'Compliance', count: q.kyc + q.sof, sub: `KYC ${q.kyc} · Source of funds ${q.sof}`, tab: (can('kyc') ? 'kyc' : 'sof') as Tab },
    { label: 'Approvals', count: q.approvals, sub: 'Desk requests waiting for a second person', tab: 'approvals' as Tab },
    can('support') && { label: 'Support', count: q.support.count, sub: q.support.count ? `Oldest open ${ago(q.support.oldestAt)}` : 'No open tickets', tab: 'support' as Tab },
  ].filter((x): x is { label: string; count: number; sub: string; tab: Tab } => !!x);
  const rows: [string, ReactNode, ReactNode][] = [
    ['Gross volume', fmtUsd(t.today.volume, 0), <Delta now={t.today.volume} before={t.yesterday.volume} money />],
    ['Net deposits', fmtUsdSigned(t.today.netDeposits, 0), <Delta now={t.today.netDeposits} before={t.yesterday.netDeposits} money />],
    ['Trades opened', String(t.today.trades), <Delta now={t.today.trades} before={t.yesterday.trades} />],
    ['New clients', String(t.today.newClients), <Delta now={t.today.newClients} before={t.yesterday.newClients} />],
    ['Open positions', t.openPositions.toLocaleString(), <span className="text-muted">now</span>],
  ];
  return (
    <>
      <SubHead title="Performance" note={`Updated ${time(h.asOf)}`} right="Today vs yesterday (UTC)" />
      <Card
        title={
          <span className="flex flex-col gap-0.5">
            Money
            <span className="text-sm text-muted">{rangeLabel(shown, today)} · UTC days</span>
          </span>
        }
        right={<TimeFrameSelect label="Money time frame (UTC days)" zone="utc" maxDays={366} presets={MONEY_PERIODS} value={period} onChange={setPeriod} />}
        clip={false}
        // Above the cards after it, so the open menu isn't drawn under them.
        className="relative z-10"
      >
        <div className="grid overflow-hidden rounded-b-[20px] sm:grid-cols-3">
          <Cell
            icon="dark"
            label="FTD"
            value={m.ftd.count.toLocaleString()}
            sub={
              <>
                <span className="block">
                  {fmtUsd(m.ftd.amount, 0)} first deposits{m.ftd.count > 0 && ` · avg ${fmtUsd(avg, 0)}`}
                </span>
                <Versus now={m.ftd.count} before={m.ftd.previous} label={before} show={m.ftd.previous.toLocaleString()} />
                {m.ftd.newClients > 0 && (
                  <span className="block">
                    {Math.round((m.ftd.fromNewClients / m.ftd.newClients) * 100)}% of {m.ftd.newClients.toLocaleString()} new clients deposited
                  </span>
                )}
              </>
            }
            onClick={can('deposits') ? () => go('deposits') : undefined}
          />
          <Cell
            icon="light"
            label="Deposits"
            value={fmtUsd(m.deposits.amount, 2)}
            sub={
              <>
                <span className="block">{m.deposits.count.toLocaleString()} deposits</span>
                <Versus now={m.deposits.amount} before={m.deposits.previous} label={before} show={fmtUsd(m.deposits.previous, 0)} />
              </>
            }
            onClick={can('deposits') ? () => go('deposits') : undefined}
          />
          <Cell
            icon="light"
            label="Withdrawals paid"
            value={fmtUsd(m.withdrawalsPaid.amount, 2)}
            sub={
              <>
                <span className="block">{q.withdrawals.count} waiting for review</span>
                <Versus now={m.withdrawalsPaid.amount} before={m.withdrawalsPaid.previous} label={before} show={fmtUsd(m.withdrawalsPaid.previous, 0)} />
              </>
            }
            onClick={can('withdrawals') ? () => go('withdrawals') : undefined}
          />
        </div>
      </Card>
      <Card title="Needs your attention" right={<span className="text-sm text-muted">{q.waiting} waiting · {q.doneToday} done today</span>}>
        <div className={cx('grid', queues.length >= 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
          {queues.map((x) => (
            <Cell key={x.label} label={x.label} value={String(x.count)} sub={x.sub} onClick={() => go(x.tab)} />
          ))}
        </div>
      </Card>
      <Card>
        <div className="flex flex-col gap-6 px-6 pt-5 pb-2">
          <h2 className="m-0 text-[21px] font-normal">Trading</h2>
          <div className="flex flex-wrap gap-x-16 gap-y-4">
            <Headline strong label="Volume" value={compactUsd(t.today.volume)} up={D.from(t.today.volume).gte(t.yesterday.volume)} />
            <Headline label="Trades" value={t.today.trades.toLocaleString()} up={t.today.trades >= t.yesterday.trades} />
            <Headline label="New clients" value={t.today.newClients.toLocaleString()} up={t.today.newClients >= t.yesterday.newClients} />
          </div>
          <VolumeChart hours={t.hours} nowHour={nowHour} />
          <table className="w-full border-collapse text-[15px]">
            <thead>
              <tr className="text-muted">
                <th className="py-2 text-left font-normal">
                  <span className="sr-only">Figure</span>
                </th>
                <th className="py-2 text-right font-normal">Today</th>
                <th className="w-36 py-2 text-right font-normal">vs yesterday</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([k, v, d]) => (
                <tr key={k}>
                  <td className="py-3 font-semibold">{k}</td>
                  <td className="num py-3 text-right">{v}</td>
                  <td className="py-3 text-right">{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ---------- Dealer ----------

interface RiskAcc {
  accountId: string;
  name: string | null;
  externalClientId: string | null;
  equity: string;
  openPnl: string;
  marginLevel: string | null;
}
interface Risk {
  tiles: { clientOpenPnl: string; grossExposure: string; unhedged: string; marginWatch: number; negativeBalances: number };
  exposure: { symbol: string; clients: number; netNotional: string | null; hedgeTargetNotional: string | null; unhedgedNotional: string | null; clientOpenPnl: string }[];
  topWinners: RiskAcc[];
  marginWatch: RiskAcc[];
  asOf: string;
}

const pct = (x: string | null) => (x === null ? '—' : `${D.from(x).mul(100).toFixed(0)}%`);
const openAccount = (id: string) => `/admin?tab=accounts&account=${id}`;

function AccountList({ title, rows, right, empty, onAll }: { title: string; rows: RiskAcc[]; right: (r: RiskAcc) => ReactNode; empty: string; onAll: () => void }) {
  return (
    <Card
      title={title}
      right={
        <button type="button" onClick={onAll} className="text-[15px] text-muted hover:text-fg">
          View all
        </button>
      }
    >
      <div className="flex flex-col px-6 py-2">
        {rows.length === 0 && <p className="m-0 py-4 text-[15px] text-muted">{empty}</p>}
        {rows.slice(0, 5).map((r) => (
          <div key={r.accountId} className="flex items-center justify-between gap-3 border-b border-line py-3 text-[15px] last:border-b-0">
            <ClientCell name={r.name} crmId={r.externalClientId} href={openAccount(r.accountId)} />
            <span className="num text-right">{right(r)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function DealerHome({ go }: { go: (t: Tab) => void }) {
  const { data: r, error } = usePoll<Risk>('/api/admin/desk/risk', 15_000);
  if (!r) return <Loading error={error} />;
  const gaps = [...r.exposure]
    .filter((e) => e.netNotional !== null)
    .sort((a, b) => D.from(b.unhedgedNotional ?? '0').abs().cmp(D.from(a.unhedgedNotional ?? '0').abs()))
    .slice(0, 8);
  return (
    <>
      <SubHead title="Dealing" note={`Live · updated ${time(r.asOf)}`} />
      <Card title="Book">
        <div className="grid sm:grid-cols-3">
          <Cell icon="dark" label="Gross exposure" value={fmtUsd(r.tiles.grossExposure, 0)} sub={`${r.exposure.length} instruments with open positions`} onClick={() => go('exposure')} />
          <Cell icon="light" label="Clients open P&L" value={fmtUsdSigned(r.tiles.clientOpenPnl, 0)} sub="The house holds the other side" onClick={() => go('positions')} />
          <Cell icon="light" label="Still to hedge" value={fmtUsd(r.tiles.unhedged, 0)} sub="After hedge targets" onClick={() => go('hedging')} />
        </div>
      </Card>
      <Card
        title="Exposure & hedging"
        right={
          <button type="button" onClick={() => go('hedging')} className="text-[15px] text-muted hover:text-fg">
            View all
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse text-[15px]">
            <thead>
              <tr className="text-sm text-muted">
                <th className="py-2.5 pl-6 text-left font-normal">Instrument</th>
                <th className="py-2.5 text-right font-normal">Clients</th>
                <th className="py-2.5 text-right font-normal">Clients net</th>
                <th className="py-2.5 text-right font-normal">Target hedge</th>
                <th className="py-2.5 pr-6 text-right font-normal">Still to hedge</th>
              </tr>
            </thead>
            <tbody>
              {gaps.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-6 text-muted">
                    No open client positions.
                  </td>
                </tr>
              )}
              {gaps.map((e) => (
                <tr key={e.symbol} className="border-t border-line">
                  <td className="py-4 pl-6 font-semibold">
                    <InstrumentLabel symbol={e.symbol} />
                  </td>
                  <td className="num py-4 text-right">{e.clients}</td>
                  <td className="num py-4 text-right">{fmtUsdSigned(e.netNotional, 0)}</td>
                  <td className="num py-4 text-right">{e.hedgeTargetNotional ? fmtUsd(D.from(e.hedgeTargetNotional).abs(), 0) : '—'}</td>
                  <td className="num py-4 pr-6 text-right font-semibold">{e.unhedgedNotional && !D.from(e.unhedgedNotional).isZero() ? fmtUsd(D.from(e.unhedgedNotional).abs(), 0) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="grid gap-2.5 md:grid-cols-2">
        <AccountList title={`Margin watch · ${r.tiles.marginWatch}`} rows={r.marginWatch} right={(a) => pct(a.marginLevel)} empty="No account is under the margin alert." onAll={() => go('risk')} />
        <AccountList title="Biggest client winners" rows={r.topWinners} right={(a) => fmtUsdSigned(a.openPnl, 0)} empty="No client is in profit right now." onAll={() => go('risk')} />
      </div>
    </>
  );
}
